import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, EmbeddedViewRef, EventEmitter, inject, Input, NgZone, OnDestroy, OnInit,
  Output, TemplateRef, ViewChild, ViewContainerRef
} from '@angular/core';
import {GlobalPositionStrategy, Overlay, OverlayRef} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import { select, Store } from '@ngrx/store';
import { SecurityState } from '@shared/store/reducers/security.reducer';
import { getUserInfo, hasFeature } from '@shared/store/selectors/security.selector';
import { ContactState, getAllContacts } from '../../../../contacts/store/contact.reducer';
import { CollaborationState } from '../../store/collaboration.reducer';
import {
  AddEmotionCommand,
  ManageParticipantsCommand
} from '../../store/collaboration.command';
import {
  ManageParticipantsAction
} from '../../store/collaboration.action';
import { animate, style, transition, trigger } from '@angular/animations';
import { PlatformSidebarState } from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';
import {
  ConversationComponentName,
  ConversationListComponentName
} from '../../store/collaboration.constants';
import { SetCurrentRightSideComponentAction } from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, EMPTY, fromEvent, merge, Observable, of, Subject } from 'rxjs';
import {catchError, debounceTime, defaultIfEmpty, distinctUntilChanged, filter, finalize, map, switchMap, takeUntil, take, timeout} from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '@shared/services/dialog-service';
import { GetAllSharedContactsAction } from '../../../../contacts/store/contact.action';
import { ShareItemService } from '@shared/services/share-item.service';
import { SnackBarService } from '@core/services/snack-bar.service';
import { ConversationBaseComponent } from '../conversation-base/conversation-base.component';
import { RightSidebarWaitScreenService } from '@core/services/right-sidebar-waitcreen.service';
import { CollaborationService } from '../../store/collaboration.service';
import {ICollaborationProviderComponent} from "@shared/components/platform-sidebar/store/view-synchronizer.interfaces";
import {ViewSynchronizerService} from "@shared/components/platform-sidebar/store/view-synchronizer.service";
import { marked } from 'marked';
import { ContactData } from '../../store/collaboration.models';
import { UserModel } from '@shared/models/user.model';
import { CallService, GroupCallCard, GroupCallPresence } from '@features/calls/call.service';
import { getConversationListInfo, getCurrentChatConversation } from '../../store/collaboration.selector';
import { ChatEditorComponent } from '@shared/components/chat-editor/chat-editor.component';
import { ChatEditorOutput } from '@shared/models/chat-editor.model';
import { MessengerDrafts } from '../../messenger/messenger-drafts';
import { CommandService } from '@core/services/command.service';
import { GraphqlContentScope } from '@shared/models/graphql-content-scope.model';
import { MessengerFlags } from '../../messenger/messenger-flags';
import { MessengerMessageActionsPort } from '../../messenger/message-actions.port';
import {
  MemberStatus, MessageCard, MessageHit, MessageReaction, PinnedMessage, TypingMember
} from '../../messenger/messenger.models';
import { MessengerPresencePort } from '../../messenger/presence.port';
import { MessengerSignalsPort } from '../../messenger/signals.port';
import { MessengerCardsPort } from '../../messenger/cards.port';
import { cardHeader } from '../../messenger/chat-list-format';
import { fileOfStructuredAttachment, filesOfAttachmentHtml, isVoiceFile, MessageFile } from '../../messenger/message-files';
import { voiceMarkOf, voiceWordsOf } from '../../messenger/voice-words';
import { VoicePlayer } from '../../messenger/voice-player.service';
import { MESSAGE_TICKS, MessageTick, tickOf } from '../../messenger/message-ticks';
import { MessageWindowActions, messageWindowActions } from '../../messenger/message-windows';
import { followsMentionRule } from '../../messenger/message-mentions';
import { membersLine, selectionLabel, ThreadMenuItem, threadMenuItems } from '../../messenger/thread-menu';
import { StarredBandState, starredBandState } from '../../messenger/starred-link';
import { activeParticipants, planMembership } from '../../messenger/membership-plan';
import { chatFaceStyle } from '../../messenger/chat-face';
import { callRowCaller, daySeparators, isAssistantAuthor, senderLine, showSender as showSenderOf } from '../../messenger/thread-layout';
import { GroupPaneAdd, GroupPaneRoleChange, GroupPaneRow } from '../group-members-panes/group-members-panes.component';
import {
  emotionTypeOf, REACTION_CHOICES, reactedByLine, reactionChangeOf, sameEmoji, withReaction
} from '../../messenger/message-reactions';
import { reactionPickerPlacement } from '../../messenger/reaction-picker-placement';
import {
  ForwardMessageDialogComponent, ForwardMessageDialogData, ForwardTarget
} from '../../messenger/forward-message-dialog/forward-message-dialog.component';
import {
  MessageInfoDialogComponent, MessageInfoDialogData
} from '../../messenger/message-info-dialog/message-info-dialog.component';
import {
  holdsMeet, START_MEET_TIMEOUT_MS, startChatMeetingCommand, startedMeetingId, startMeetError
} from '../../messenger/start-meet';
import {scheduleMeetingCommand, scheduleMeetingGuests, scheduleMeetingSlot} from '../../messenger/schedule-meeting';
import {holdsMessages, OPEN_IN_MESSAGES, openInMessages} from '../../messenger/open-in-messages';

/**
 * How near the top of the thread's box the next older page is asked for (ITP-E-240-11): about three rows, so the page is
 * usually in before the reader reaches the first message loaded.
 */
export const OLDER_PAGE_THRESHOLD_PX = 200;

@Component({
    standalone: false,
    selector: 'conversation',
  templateUrl: './conversation.component.html',
  styleUrls: ['./conversation.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: '0' }),
        animate('.5s ease-out', style({ opacity: '1' }))
      ])
    ])
  ]
})
export class ConversationComponent extends ConversationBaseComponent
  implements OnInit, AfterViewInit, ICollaborationProviderComponent, OnDestroy {
  @Input() focusEditorAutomaticallyOnInit: boolean;
  @Input() isFloatingView = false;
  @Input() hideSettings = false;
  @Output() closeConversationWindow$ = new EventEmitter<any>();
  /**
   * Whether the Messages page's Details shows (ITP-E-194-02): the right sidebar open on its Details panel with the
   * page's item in it (ITP-E-204-01), so the header's ⓘ reads pressed. The page host only.
   */
  @Input() detailsOpen = false;
  /** The header's ⓘ, on the Messages page only: the page opens Details in the sidebar, or closes it (ITP-E-204-01). */
  @Output() detailsToggled = new EventEmitter<void>();
  hasFeature$: Observable<any>;
  data: any;
  allContacts$: any;
  allContacts: Array<any> = [];
  newChat$: any;
  currentUrl: any;
  isTitleEditing: boolean;
  /**
   * The live call of this group conversation, for the header (ITP-E-172-01), with what this tab may do about it
   * (ITP-E-175-05): Join, nothing while it is in it here ("In call"), or Switch here while another tab, browser or
   * device holds it. Null for a 1:1, or when no call is live.
   */
  liveGroupCall$: Observable<{card: GroupCallCard, presence: GroupCallPresence} | null>;
  /**
   * Whether the header offers Start a Meet (ITP-E-208-01): the user holds Orbitax Meet (OM). The licence alone, as the
   * server checks it (D-09): the calls switch that hides Voice call and Video call does not hide it.
   */
  canStartMeet$: Observable<boolean>;
  /** Whether the reader holds the Messages page (OM-Messages), which the thread ⋯'s Open in Messages needs (ITP-E-268-01). */
  holdsMessages = false;
  /** A Start a Meet press waiting for the server, so a second press does not open a second blank tab meanwhile. */
  private startingMeet = false;
  /** The composer, read for the conversation's draft (ITP-E-194-03). */
  @ViewChild(ChatEditorComponent) private chatEditor: ChatEditorComponent;
  /** The conversation the composer's text belongs to. */
  private draftConversationId: string = null;
  private readonly composerChanged$ = new Subject<void>();
  /** The six the reaction picker offers (ITP-E-194-05). */
  readonly reactionChoices = REACTION_CHOICES;
  /** The reactions under each message, by message id (B-10), with this user's own added on top. */
  reactions: Record<string, MessageReaction[]> = {};
  /** The message whose reaction bar is open. */
  reactingTo: string = null;
  /** The open bar shows every emoji in place of its six (B-10). */
  pickingAnyEmoji = false;
  /**
   * THE REACTION BAR'S OVERLAY (ITP-E-240-09): one for the whole thread, made the first time a bar opens, not one per
   * message. The bar's template is drawn into it from its own place in this view, so it is checked with the thread.
   */
  @ViewChild('reactionBar', {static: true}) private reactionBarTemplate: TemplateRef<unknown>;
  @ViewChild('reactionBar', {static: true, read: ViewContainerRef}) private reactionBarSlot: ViewContainerRef;
  private reactionBar: OverlayRef = null;
  private reactionBarView: EmbeddedViewRef<unknown> = null;
  /** The React button the open bar is anchored to. */
  private reactionAnchor: HTMLElement = null;
  private readonly reactionBarClosed$ = new Subject<void>();
  private readonly overlay = inject(Overlay);
  private readonly zone = inject(NgZone);
  /** ITP-E-249-05: the tab's voice player, which keeps the voice messages this user has played. */
  private readonly voicePlayer = inject(VoicePlayer);
  private reactionsConversationId: string = null;
  /** The ids of the messages this user starred in the open conversation (B-12). */
  starredIds = new Set<string>();
  /** The conversation's pinned messages, newest first, up to 3 (B-13). */
  pinnedMessages: PinnedMessage[] = [];
  /** Which pin the bar under the header shows; a click on the bar moves it on to the next. */
  pinnedIndex = 0;
  /** Whether the search bar under the header is open (ITP-E-194-07, B-16). */
  searchOpen = false;
  searchText = '';
  /** What the search found, oldest first; `searchIndex` is the one the thread is on. */
  searchHits: MessageHit[] = [];
  searchIndex = -1;
  /** The text the hits answer, once the port has answered it. */
  private searchedText = '';
  private readonly searchText$ = new Subject<string>();
  /** A found message's text with the matches marked, kept per message id against the text it was made from. */
  private searchMarkup: Record<string, {source: string, query: string, html: string}> = {};
  @ViewChild('threadSearchInput') private threadSearchInput: ElementRef<HTMLInputElement>;
  /** The glyph and words of each delivery tick (ITP-E-194-08). */
  readonly messageTicks = MESSAGE_TICKS;
  /** B-17: the sequence of the open conversation delivered to every member; null while not known. */
  private deliveredUpTo: number | null = null;
  private deliveredConversationId: string = null;
  /** The unread divider: the message it stands before, and how many were unread when the conversation opened. */
  unreadDivider: {messageId: string, count: number} | null = null;
  private dividerConversationId: string = null;
  /** Whether the open conversation's divider is still to be placed, on the first load of its messages. */
  private dividerPending = false;
  /** B-18: the other members typing in the open conversation now. */
  typingMembers: TypingMember[] = [];
  /** When this user's typing was last reported, and in which conversation: at most once per 3 s. */
  private typingReportedAt = 0;
  private typingReportedFor: string = null;
  /** B-15: the dot on a direct conversation's header face. */
  headerStatus: MemberStatus | null = null;
  /** B-15, on the Messages page: how many of a group's members are online; null until presence answers. */
  headerOnline: number | null = null;

  // CARBON'S THREAD HEADER AND BAND (ITP-E-203-03). The face is 36px; a group's is two members at 68% of that.
  readonly faceStyle = chatFaceStyle(36);
  readonly groupFaceStyle = chatFaceStyle(24);
  readonly groupFaceRingStyle = chatFaceStyle(24, true);
  /** The menu's words and glyphs, per item (carbon's `MenuItem` icons). */
  readonly headerMenu: Readonly<Record<Exclude<ThreadMenuItem, 'divider'>, {icon: string, label: string}>> = {
    search: {icon: 'fa-regular fa-magnifying-glass', label: 'Search'},
    select: {icon: 'fa-regular fa-square-check', label: 'Select'},
    starred: {icon: 'fa-regular fa-star', label: 'Starred'},
    members: {icon: 'fa-regular fa-users', label: 'Members'},
    rename: {icon: 'fa-regular fa-pen', label: 'Rename'},
    leave: {icon: 'fa-regular fa-arrow-right-from-bracket', label: 'Leave'},
    delete: {icon: 'fa-regular fa-trash-can', label: 'Delete'},
    open: OPEN_IN_MESSAGES
  };
  /** The strip under the header: the selection's controls, or the starred messages. The search bar is the third mode. */
  band: 'none' | 'select' | 'starred' = 'none';
  /** The messages picked in the Select band, by id. */
  selectedIds = new Set<string>();
  /** The group's Members | Add panes, swapped in over the thread on the Chat panel (ITP-E-203-05). */
  membersOpen = false;
  private headerFacesKey: string = null;
  private dayLabelsKey: string = null;
  private dayLabelsCache: (string | null)[] = [];
  private headerFacesCache: {email: string, name: string}[] = [];
  /** Today's attachment HTML read into files, per message id against the HTML it was read from (ITP-E-194-10). */
  private attachmentFiles: Record<string, {source: string, files: MessageFile[] | null}> = {};
  /** B-19: each message's structured attachments, as files, by message id. */
  private structuredFiles: Record<string, MessageFile[]> = {};
  /** B-20: each message's card, by message id. */
  private messageCards: Record<string, MessageCard> = {};
  private cardsConversationId: string = null;

  constructor(public collaborationStore: Store<CollaborationState>,
    private platformSidebarStore: Store<PlatformSidebarState>,
    public dialog: MatDialog,
    private contactStore: Store<ContactState>,
    public snackbarService: SnackBarService,
    private router: Router,
    private collaborationService: CollaborationService,
    private activatedRoute: ActivatedRoute,
    private changedetector: ChangeDetectorRef,
    public securityStore: Store<SecurityState>,
    public dialogService: DialogService,
    public rightSidebarWaitScreenService: RightSidebarWaitScreenService,
    public viewSynchronizerService: ViewSynchronizerService,
    protected shareItemService: ShareItemService,
    private callService: CallService,
    private messengerDrafts: MessengerDrafts,
    private commandService: CommandService,
    private messengerFlags: MessengerFlags,
    private messageActions: MessengerMessageActionsPort,
    private signals: MessengerSignalsPort,
    private presence: MessengerPresencePort,
    private cardsPort: MessengerCardsPort) {
    super(collaborationStore, snackbarService, dialog, dialogService, securityStore, rightSidebarWaitScreenService);
    this.hasLeft = false;
    this.viewSynchronizerService.setProvider(this);
    this.focusEditorAutomaticallyOnInit = true;
    this.conversationListComponentName = ConversationListComponentName;
    this.conversationComponentName = ConversationComponentName;
  }

  ngOnInit() {
    this.hasFeature$ = this.securityStore.pipe(select(hasFeature));
    this.watchStartMeet();

    this.initComponent();
    // Ahead of the thread's own subscription, which marks the conversation read: the divider reads the count first.
    this.watchSignals();
    this.watchPresence();
    this.watchOnlineCount();
    this.watchBand();
    this.SubscribeToCurrentChatConversation();
    this.keepComposerDrafts();
    this.watchReactions();
    this.watchReactionEvents();
    this.watchCards();
    this.watchStarsAndPins();
    this.watchPlayedVoice();
    this.watchThreadSearch();
    this.subscribeToDeleteMessageNotification();
    this.securityStore.select(getUserInfo).pipe(takeUntil(this.unsubscribe$)).subscribe(currentUser => {
      this.currentUser = currentUser;
      this.initDefaultRoles();
    });
    this.allContacts$ = this.contactStore.select(getAllContacts).pipe(takeUntil(this.unsubscribe$));
    this.allContacts$.subscribe(data => {
      // console.log(`contact`, data);
      if (data) {
        this.allContacts = data.raw;
        if (this.currentChatConversation?.sourceType === 'OrbitaxSupport') {
          this.filterContacts();
          this.addXatBotToContacts();
        }
      }
    });

    if (!this.focusEditorAutomaticallyOnInit) {
      this.focusEditorOnChatEditor$.next(false);
    }
    this.watchGroupCall();
    // A call to this conversation's person went unanswered and the caller chose Send a message (ITP-E-170-02).
    this.callService.messageRequested$.pipe(takeUntil(this.unsubscribe$)).subscribe(email => {
      if (this.callPeer?.email?.toLowerCase() === email?.toLowerCase()) {
        this.focusEditorOnChatEditor$.next(true);
      }
    });
    // Reply with message from the incoming call (ITP-E-191-01) opens a chat in a panel that may already be showing
    // one: the composer takes the focus once the replied-to chat is the one shown.
    this.callService.replyRequested$.pipe(
      switchMap(conversationId => this.collaborationStore.select(getCurrentChatConversation).pipe(
        filter(conversation => conversation?.id === conversationId), take(1))),
      takeUntil(this.unsubscribe$)
    ).subscribe(() => this.focusEditorOnChatEditor$.next(true));
  }
  /**
   * The one other person in a 1:1 conversation of the user's own company, who the header's Call rings
   * (ITP-E-170-01). Null for a group, a support chat or a cross-company chat (D-15), so no Call shows.
   */
  get callPeer(): UserModel | null {
    const conversation = this.currentChatConversation;
    if (!conversation || conversation.isGroupConversation || conversation.sourceType === 'OrbitaxSupport' || !this.currentUser) {
      return null;
    }
    const members = (conversation.roles || []).filter(role => role?.user?.email && !role.hasLeft);
    const others = members.filter(role => role.user.email.toLowerCase() !== this.currentUser.email.toLowerCase());
    if (members.length !== 2 || others.length !== 1) {
      return null;
    }
    const peer: UserModel = others[0].user;
    return !peer.companyId || peer.companyId.toLowerCase() === this.currentUser.companyId?.toLowerCase() ? peer : null;
  }

  /** Whether this user may call at all — the Orbitax Meet licence and the calls switch (ITP-E-170-03). */
  get canCall$(): Observable<boolean> {
    return this.callService.canCall$;
  }

  onCall(): void {
    this.callPeerAs(false);
  }

  /** The Video call button (ITP-E-182-05): rings as a video call, and once answered the card opens with the camera on. */
  onVideoCall(): void {
    this.callPeerAs(true);
  }

  private callPeerAs(video: boolean): void {
    const peer = this.callPeer;
    if (peer) {
      const name = `${peer.firstName || ''} ${peer.lastName || ''}`.trim() || peer.email;
      // This chat's id goes with the call (D-18, ITP-E-189-11): the server posts the call's row here.
      this.callService.start(peer.email, name, video, this.currentChatConversation?.id).then();
    }
  }

  /** Whether this is a group conversation the header may call (ITP-E-172-01). */
  get isGroupCallable(): boolean {
    const conversation = this.currentChatConversation;
    return !!conversation?.isGroupConversation && conversation.sourceType !== 'OrbitaxSupport';
  }

  /** Calls the group: up to 8 members ring, a larger group gets only the card (ITP-E-172-01). */
  onCallGroup(): void {
    this.callGroupAs(false);
  }

  /** Video-calls the group (ITP-E-182-05): the members ring as video, and the card opens with this side's camera on. */
  onVideoCallGroup(): void {
    this.callGroupAs(true);
  }

  private callGroupAs(video: boolean): void {
    if (this.isGroupCallable) {
      this.callService.startGroup(this.currentChatConversation.id, this.currentChatConversation.title, video).then();
    }
  }

  onJoinGroupCall(call: GroupCallCard): void {
    this.callService.joinGroup(call.invitationId, call.conversationId, this.currentChatConversation?.title).then();
  }

  /** Moves the call this user holds in another tab or browser into this one (ITP-E-175-05). */
  onSwitchGroupCallHere(call: GroupCallCard): void {
    this.callService.switchGroupCallHere(call.invitationId, call.conversationId, this.currentChatConversation?.title).then();
  }

  /**
   * Start a Meet shows to a user who holds Orbitax Meet (ITP-E-208-01, D-09), read from the security store's features;
   * Open in Messages to one who holds the Messages page (ITP-E-268-01).
   */
  private watchStartMeet(): void {
    this.canStartMeet$ = (this.hasFeature$ || of(null)).pipe(map(holdsMeet), distinctUntilChanged());
    (this.hasFeature$ || of(null)).pipe(map(holdsMessages), distinctUntilChanged(), takeUntil(this.unsubscribe$))
      .subscribe(holds => this.holdsMessages = holds);
  }

  /**
   * Start a Meet (ITP-E-208-01): the server makes a new Orbitax Meet for this chat's members and posts its card here;
   * nobody is rung, and every press is a new meeting (D-02, D-03). The tab opens during the click, so a popup blocker
   * allows it, and goes to the Meet room on this origin once the server answers, as Open in Meet's does; a refused or
   * unanswered start closes it. A blocked tab gets one more try, opened with the answer.
   */
  onStartMeet(): void {
    const conversationId = this.currentChatConversation?.id;
    if (!conversationId || this.startingMeet) {
      return;
    }
    const room = window.open('', '_blank');
    this.startingMeet = true;
    this.commandService.executePlatformCommand(startChatMeetingCommand(conversationId)).pipe(
      take(1),
      timeout(START_MEET_TIMEOUT_MS),
      catchError(() => of(null)),
      defaultIfEmpty(null),
      finalize(() => this.startingMeet = false)
    ).subscribe(response => {
      const meetId = startedMeetingId(response);
      if (!meetId) {
        room?.close();
        this.snackbarService.showError(startMeetError(response));
        return;
      }
      const url = CallService.meetUrl(meetId);
      if (room && !room.closed) {
        room.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
    });
  }

  /**
   * Schedule meeting (ITP-E-208-04, ITP-E-348-02): the calendar's meeting dialog, which the server opens on the next half
   * hour with this chat's members of the user's company ticked (not the user, who organises it) and the chat's Meet locked
   * on. Saving it creates the event and its Meet, binds the Meet to this chat and posts the event card here.
   */
  onScheduleMeeting(): void {
    const conversation = this.currentChatConversation;
    if (!conversation?.id) {
      return;
    }
    const command = scheduleMeetingCommand(conversation.id, scheduleMeetingGuests(conversation, this.currentUser), scheduleMeetingSlot());
    this.commandService.executePlatformCommand(command).pipe(takeUntil(this.unsubscribe$)).subscribe({
      error: failure => console.error('CreateCalendarEventCommand failed', failure)
    });
  }

  /**
   * The conversation's call (ITP-E-172-01, and a 1:1 chat's since ITP-E-189-11): on opening it, ask the server whether
   * one is live — and where this user's part of it is held (ITP-E-175-05) — so a group's header offers Join or Switch
   * here and the chat's call row reads live; and while one is live, make sure its row is in the messages even if the
   * call started after they loaded.
   */
  private watchGroupCall(): void {
    const conversation$ = this.collaborationStore.select(getCurrentChatConversation).pipe(takeUntil(this.unsubscribe$));
    conversation$.pipe(
      filter(conversation => !!conversation?.id && conversation.sourceType !== 'OrbitaxSupport'),
      map(conversation => conversation.id),
      distinctUntilChanged()
    ).subscribe(id => this.callService.loadConversationCall(id).then());

    this.liveGroupCall$ = combineLatest([conversation$, this.callService.groupCalls$, this.callService.state$]).pipe(
      map(([conversation, cards, state]) => {
        const card = conversation?.isGroupConversation ? cards[conversation.id] : null;
        return card?.live ? {card, presence: CallService.groupCallPresence(card.invitationId, card, state)} : null;
      }));

    combineLatest([conversation$, this.callService.groupCalls$]).pipe(takeUntil(this.unsubscribe$))
      .subscribe(([conversation, cards]) => this.showLiveCallRow(conversation?.id ? cards[conversation.id] : null));
  }

  /**
   * The live row is the stored message (D-18, ITP-E-189-11): it arrives through the normal message publish like any
   * message. Should CallStarted get here first, the row is appended from what it names — the stored message's id, the
   * caller — so there is never a row of the tab's own, and the publish, or the next load, takes its place by id. It
   * follows the call's state in CallService like any card (ITP-E-180-08, ITP-E-189-10).
   */
  private showLiveCallRow(card: GroupCallCard | null): void {
    const row = ConversationComponent.liveRowFor(card, this.messages, this.currentUser?.email);
    if (row) {
      this.messages = [...this.messages, row];
      this.scrollToBottom();
    }
  }

  /**
   * The message to append for a live call the loaded messages do not have yet: the stored row, by its own id, in the
   * caller's column for the caller (`isMine` from the caller's email, on every tab); null when the call is not live, the
   * row is already there (by id or by call), or the server has not named the row.
   */
  static liveRowFor(card: GroupCallCard | null | undefined, messages: any[] | undefined, currentUserEmail: string | undefined): any | null {
    if (!card?.live || !card.cardMessageId || !messages) {
      return null;
    }
    if (messages.some(message => message.id === card.cardMessageId || message.callInvitationId === card.invitationId)) {
      return null;
    }
    const mine = !!card.callerEmail && !!currentUserEmail && card.callerEmail.toLowerCase() === currentUserEmail.toLowerCase();
    return {
      id: card.cardMessageId,
      isMine: mine || (!card.callerEmail && card.isCaller === true),
      callInvitationId: card.invitationId,
      callState: 'Live',
      callerName: card.callerName,
      callerEmail: card.callerEmail,
      createdBy: {email: card.callerEmail},
      ...(card.mode === 'video' ? {callType: 'Video'} : {}),
      createdDate: new Date().toISOString()
    };
  }

  filterContacts() {
    this.allContacts = this.allContacts.filter(contact => {
      const isValidContact = (contact.email.toLowerCase() !== this.currentUser.email.toLowerCase()) &&
        (this.currentChatConversation.roles.findIndex(role =>
          role.user.email.toLowerCase() === contact.email.toLowerCase()) !== -1);
    return isValidContact;
    });
  }
  ngAfterViewInit() {
    this.watchThreadTop();
  }

  ngOnDestroy() {
    this.reactionBarClosed$.next();
    this.reactionBar?.dispose();
    this.reactionBar = null;
    this.saveComposerDraft();
    super.ngOnDestroy();
  }

  /**
   * OLDER MESSAGES LOAD AS THE READER SCROLLS UP (ITP-E-240-11, owner review: "how WhatsApp do this?"). A scroll of the
   * thread's box near its top asks for the next older page, and so does the thread settling (placed at its newest message,
   * or a page landed with the reader's place kept) while the box is still near its top: that is how a box that is not full
   * fills up. The scroll is listened to outside Angular, and only a page asked for enters it.
   */
  private watchThreadTop(): void {
    const box: HTMLElement = this.messagecontainer?.nativeElement;
    if (!box) {
      return;
    }
    this.zone.runOutsideAngular(() => fromEvent(box, 'scroll', {passive: true}).pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.pageOlderNearTop()));
    // After a failed page only the reader's scroll or Try again asks again: the thread never retries by itself.
    this.threadSettled$.pipe(filter(() => !this.olderPageFailed), takeUntil(this.unsubscribe$))
      .subscribe(() => this.pageOlderNearTop());
  }

  /**
   * The next older page, when the thread has been placed, its box is near the top and a page may be asked for; in the
   * Starred band, only while a starred message is still older than those loaded.
   */
  private pageOlderNearTop(): void {
    const box: HTMLElement = this.messagecontainer?.nativeElement;
    if (!box || !this.threadPlaced || box.scrollTop > OLDER_PAGE_THRESHOLD_PX || !this.canLoadOlder()
      || (this.band === 'starred' && this.starredState !== 'older-starred')) {
      return;
    }
    this.zone.run(() => this.loadOlderMessages());
  }

  /**
   * THE COMPOSER DRAFT (ITP-E-194-03): what is typed and not sent is kept per conversation in this browser, put back
   * when the conversation opens again, and cleared by sending it or emptying the composer. The list marks the row
   * "Draft: …" from the same store, in the panel and on the Messages page.
   */
  private keepComposerDrafts(): void {
    this.collaborationStore.select(getCurrentChatConversation).pipe(
      map(conversation => conversation?.id || null),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$)
    ).subscribe(id => {
      // The composer still holds the conversation being left.
      this.saveComposerDraft();
      this.draftConversationId = id;
      const draft = this.messengerDrafts.get(id);
      if (this.chatEditor?.editable) {
        this.chatEditor.editable.innerHTML = draft;
      } else {
        this.chatText = draft;
      }
    });
    // Read once typing settles, from the composer itself: a send empties it, and the draft goes with it.
    this.composerChanged$.pipe(debounceTime(300), takeUntil(this.unsubscribe$)).subscribe(() => this.saveComposerDraft());
  }

  private saveComposerDraft(): void {
    if (this.draftConversationId && this.chatEditor?.editable) {
      this.messengerDrafts.save(this.draftConversationId, this.chatEditor.editable.innerHTML);
    }
  }

  /**
   * THE REACTIONS UNDER A MESSAGE (ITP-E-194-05). Adding one is today's `AddEmotionCommand`, on in every build. What
   * everyone else reacted, the counts, and taking one's own back are B-10: read through the message actions port, so
   * a production build shows only what this user added here until it ships.
   */
  private watchReactions(): void {
    this.collaborationStore.select(getCurrentChatConversation).pipe(takeUntil(this.unsubscribe$)).subscribe(conversation => {
      if ((conversation?.id || null) !== this.reactionsConversationId) {
        this.reactionsConversationId = conversation?.id || null;
        this.reactions = {};
        this.closeReactionPicker(false);
      }
      if (!conversation?.id || !this.messengerFlags.isOn('B-10')) {
        return;
      }
      // Only the messages not asked about yet: a new message must not reset the rows already drawn.
      const ids = (conversation.messages || [])
        .filter(message => message?.id && !message.isTemporary && !message.callInvitationId && !(message.id in this.reactions))
        .map(message => message.id);
      if (!ids.length) {
        return;
      }
      ids.forEach(id => this.reactions[id] = []);
      this.messageActions.reactions(conversation.id, ids).pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: found => {
          if (conversation.id === this.reactionsConversationId) {
            this.reactions = {...this.reactions, ...found};
          }
        },
        error: () => undefined
      });
    });
  }

  /**
   * B-10 (ITP-E-207-04): what is reacted or taken back while the thread is open, by anyone, this user's other tabs too. Each
   * `AddEmotion` and `RemoveEmotion` event moves the count, `mine` and who reacted of a message whose reactions were read.
   * This tab's own add and take-back are already drawn, and applying one again changes nothing.
   */
  private watchReactionEvents(): void {
    if (!this.actions || !this.messengerFlags.isOn('B-10')) {
      return;
    }
    this.actions.pipe(
      filter((action: any) => action?.type === '[OTHERS] AddEmotion' || action?.type === '[OTHERS] RemoveEmotion'),
      takeUntil(this.unsubscribe$)
    ).subscribe((action: any) => {
      const notification = action.payload;
      const change = reactionChangeOf(action.type.replace('[OTHERS] ', ''), notification?.Payload ?? notification?.payload);
      if (!change || !(change.messageId in this.reactions)) {
        return;
      }
      const isReader = change.email === this.currentUser?.email?.toLowerCase();
      this.reactions = {
        ...this.reactions,
        [change.messageId]: withReaction(this.reactions[change.messageId], change.emoji, change, change.added, isReader)
      };
    });
  }

  /**
   * THE FILES AND CARDS ON A MESSAGE (ITP-E-194-10). Today's attachment is HTML and is read into file cards on every
   * build. What a message carries besides, its structured attachments (B-19) and its card (B-20), is read through the
   * cards port for the messages not asked about yet, so a production build shows neither until they ship. A call row is
   * never asked about: call cards are ITP-P-34's (D-12).
   */
  private watchCards(): void {
    const structured = this.messengerFlags.isOn('B-19');
    const cards = this.messengerFlags.isOn('B-20');
    this.collaborationStore.select(getCurrentChatConversation).pipe(takeUntil(this.unsubscribe$)).subscribe(conversation => {
      if ((conversation?.id || null) !== this.cardsConversationId) {
        this.cardsConversationId = conversation?.id || null;
        this.attachmentFiles = {};
        this.structuredFiles = {};
        this.messageCards = {};
      }
      if (!conversation?.id || (!structured && !cards)) {
        return;
      }
      const ids = (conversation.messages || [])
        .filter(message => message?.id && !message.isTemporary && !message.callInvitationId && !message.isSystemMessage
          && !(message.id in this.structuredFiles))
        .map(message => message.id);
      if (!ids.length) {
        return;
      }
      ids.forEach(id => this.structuredFiles[id] = []);
      const conversationId = conversation.id;
      if (structured) {
        this.cardsPort.attachments(conversationId, ids).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: found => {
            if (conversationId === this.cardsConversationId) {
              const files: Record<string, MessageFile[]> = {};
              Object.keys(found).forEach(id => files[id] = (found[id] || []).map(fileOfStructuredAttachment));
              this.structuredFiles = {...this.structuredFiles, ...files};
            }
          },
          error: () => undefined
        });
      }
      if (cards) {
        this.cardsPort.cards(conversationId, ids).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: found => {
            if (conversationId === this.cardsConversationId) {
              this.messageCards = {...this.messageCards, ...found};
            }
          },
          error: () => undefined
        });
      }
    });
  }

  /**
   * The files a message's bubble shows as cards: its structured attachments when B-19 gave it any (they replace the
   * HTML), else its attachment HTML read into files. Null when there is nothing to show as cards, or the HTML could not
   * be read, so the bubble renders the attachment as it always has.
   */
  filesOf(message: any): MessageFile[] | null {
    // ITP-E-249-01 (B-25): a voice message is drawn as its voice bubble (voiceOf), never as a file card.
    const structured = message?.id && this.structuredFiles[message.id]?.filter(file => !this.voiceOn || !isVoiceFile(file));
    if (structured?.length) {
      return structured;
    }
    const source: string = message?.attachment || '';
    if (!source.trim()) {
      return null;
    }
    const key = message.id || source;
    const cached = this.attachmentFiles[key];
    if (cached && cached.source === source) {
      return cached.files;
    }
    const files = filesOfAttachmentHtml(source);
    this.attachmentFiles[key] = {source, files};
    return files;
  }

  /**
   * ITP-E-249-01 (B-25): the voice message's clip, which the bubble draws as a voice bubble in place of its files, or null for
   * any other message. A voice message's only content is its clip.
   * ITP-E-249-02: a voice message on its way (the temporary Sending message) carries its clip itself (`localVoice`), which
   * plays from memory; the sent message that replaces it draws the same attachment, so its bubble takes over.
   */
  voiceOf(message: any): MessageFile | null {
    if (!this.voiceOn) {
      return null;
    }
    if (message?.isTemporary) {
      return message.localVoice || null;
    }
    return (message?.id && this.structuredFiles[message.id]?.find(isVoiceFile)) || null;
  }

  /** ITP-E-249-01: whether voice messages are on (B-25); off, a clip is drawn as the file it is. */
  private get voiceOn(): boolean {
    return this.messengerFlags.isOn('B-25');
  }

  /**
   * ITP-E-249-06 (B-25): a voice message in words, "Voice message (0:42)", by its mark; null for any other message. What a reply's
   * quote shows for a voice message, which has no text.
   */
  voiceWordsOf(message: any): string | null {
    return voiceWordsOf(message);
  }

  /**
   * ITP-E-249-06 (B-25): whether `message` is a voice message: a sent one by its mark, one on its way by its clip. It has no text,
   * so Edit is never offered on it, even on one that has just arrived live and whose `canEdit` the server has not answered yet.
   */
  isVoiceMessage(message: any): boolean {
    return voiceMarkOf(message) !== null || !!this.voiceOf(message);
  }

  /** B-20: the message's card, or null. */
  cardOf(message: any): MessageCard | null {
    return (message?.id && this.messageCards[message.id]) || null;
  }

  /**
   * A card row's card, drawn alone in the thread with no bubble and no sentence, as Google Chat shows a Meet
   * (ITP-E-240-06): a row posted for its card (`card.kind`: Start a Meet, Schedule meeting, a pasted link's card) whose card
   * is here to draw. Null for any other message, and for a card row whose card is not, which keeps its stored text in its
   * bubble. The stored text stays what text-only readers get: the list preview, search, notifications, a reply's quote.
   */
  standaloneCardOf(message: any): MessageCard | null {
    return message?.card?.kind ? this.cardOf(message) : null;
  }

  /** The header line of a standalone card: who did what, "You" on the reader's own (chat-list-format `cardHeader`). */
  cardHeaderOf(message: any): string | null {
    return cardHeader(message);
  }

  reactionsOf(message: any): MessageReaction[] {
    return (message?.id && this.reactions[message.id]) || [];
  }

  /**
   * Whether a message takes a messenger action (react, forward, star, pin): a sent message of the thread, not a call
   * row, a removed or a system one.
   */
  canActOn(message: any): boolean {
    return !this.hasLeft && !!message?.id && !message.isTemporary && !message.isDeleted && !message.isSystemMessage
      && !message.callInvitationId;
  }

  canReact(message: any): boolean {
    return this.canActOn(message);
  }

  /**
   * Edit and Delete for everyone on a message (ITP-E-207-08, story 17): in a chat, as the server's windows allow this reader
   * (`canEdit`, `canDeleteForEveryone`); in another conversation kind, the author's own as before (messenger/message-windows.ts).
   */
  windowActions(message: any): MessageWindowActions {
    return messageWindowActions(message);
  }

  /**
   * ITP-E-207-07 (B-19): the member chat the composer sends a file from this computer to, as the chat's own file; none in
   * any other conversation, whose paperclip attaches a Drive file's link as before.
   */
  get chatFilesConversationId(): string | null {
    return this.messengerFlags.isOn('B-19') && followsMentionRule(this.currentChatConversation) ? this.currentChatConversation.id : null;
  }

  /**
   * MOBILE-E-104-03 (ADR 0177): XatBot and Sara can be asked, by @ or the +, in any member chat, a personal one too; the
   * server brings them in and they answer in the chat. Not in support or a hosted conversation, which keep their own.
   */
  get assistantsInConversation(): boolean {
    return followsMentionRule(this.currentChatConversation);
  }

  /** Taking one's own reaction back, and reacting with any emoji, need B-10's backend. */
  get canRemoveReaction(): boolean {
    return this.messengerFlags.isOn('B-10');
  }

  get canReactWithAnyEmoji(): boolean {
    return this.messengerFlags.isOn('B-10');
  }

  /**
   * THE REACTION BAR (ITP-E-240-09, owner review: "like WhatsApp, up the emoji icon"). A message's React button opens it
   * over the thread, anchored to the button, or closes it when it is open on that message. One bar at a time: another
   * message's button is a press outside the open one, which closes it, and then opens it there.
   */
  toggleReactionPicker(message: any, anchor?: HTMLElement): void {
    if (this.reactingTo && this.reactingTo === message?.id) {
      this.closeReactionPicker();
      return;
    }
    this.closeReactionPicker(false);
    if (!message?.id) {
      return;
    }
    this.reactingTo = message.id;
    this.pickingAnyEmoji = false;
    this.reactionAnchor = anchor || null;
    // Placed by script (placeReactionPicker), so the CDK's own flip, which is bounded by the window, never runs.
    this.reactionBar ??= this.overlay.create({
      positionStrategy: this.overlay.position().global(),
      panelClass: 'messenger-reaction-picker-pane'
    });
    this.reactionBarView = this.reactionBar.attach(new TemplatePortal(this.reactionBarTemplate, this.reactionBarSlot));
    this.placeReactionPicker();
    this.focusReactionPicker();
    this.watchReactionPicker(this.reactionBar);
  }

  /** The bar's "+": every emoji in place of the six, in the same overlay, measured and placed again (B-10). */
  showEveryEmoji(): void {
    this.pickingAnyEmoji = true;
    this.reactionBarView?.detectChanges();
    this.placeReactionPicker();
    this.focusReactionPicker();
  }

  /** A key of the bar, or one of every emoji: a reaction on the message the bar is open on. */
  reactFromPicker(glyph: string): void {
    const message = (this.messages || []).find(each => each?.id === this.reactingTo);
    if (message) {
      this.react(message, glyph);
    } else {
      this.closeReactionPicker();
    }
  }

  /**
   * Closes the bar: on a pick, a press outside it, Escape, a scroll, a chat change, the select band. Focus goes back to the
   * React button when it was in the bar or nowhere; a press that put it somewhere else, such as the composer, keeps it there.
   */
  closeReactionPicker(restoreFocus = true): void {
    const anchor = this.reactionPickerAnchor();
    const focused = document.activeElement;
    const focusWasInBar = !focused || focused === document.body || !!this.reactionBar?.overlayElement.contains(focused);
    this.reactingTo = null;
    this.pickingAnyEmoji = false;
    this.reactionAnchor = null;
    this.reactionBarView = null;
    this.reactionBarClosed$.next();
    if (this.reactionBar?.hasAttached()) {
      this.reactionBar.detach();
    }
    if (restoreFocus && focusWasInBar && anchor?.isConnected) {
      anchor.focus({preventScroll: true});
    }
  }

  /** The open bar's React button: the one pressed, or the same message's when its row was drawn again since. */
  private reactionPickerAnchor(): HTMLElement | null {
    if (this.reactionAnchor?.isConnected) {
      return this.reactionAnchor;
    }
    const box: HTMLElement = this.messagecontainer?.nativeElement;
    return this.reactingTo && box
      ? box.querySelector<HTMLElement>(`.message-row[data-message-id="${CSS.escape(this.reactingTo)}"] .message-react`)
      : null;
  }

  /**
   * Carbon's placement (messenger/reaction-picker-placement.ts) from what is on the screen: the bar's own size, its button,
   * and the thread's scroll box, which bounds it. Measured with the bar at the corner, so nothing squeezes it, then moved;
   * both happen before the browser paints.
   */
  private placeReactionPicker(): void {
    const bar = this.reactionBar;
    const anchor = this.reactionPickerAnchor();
    const box: HTMLElement = this.messagecontainer?.nativeElement;
    if (!bar?.hasAttached() || !anchor || !box) {
      return;
    }
    const position = bar.getConfig().positionStrategy as GlobalPositionStrategy;
    position.left('0px').top('0px');
    bar.updatePosition();
    const {width, height} = bar.overlayElement.getBoundingClientRect();
    const {top, left} = reactionPickerPlacement(anchor.getBoundingClientRect(), {width, height}, box.getBoundingClientRect());
    position.left(`${left}px`).top(`${top}px`);
    bar.updatePosition();
  }

  /** Focus into the bar: its first key, or the search of every emoji. */
  private focusReactionPicker(): void {
    const pane = this.reactionBar?.overlayElement;
    if (pane && !pane.contains(document.activeElement)) {
      pane.querySelector<HTMLElement>('.reaction-picker-key, emoji-mart input')?.focus({preventScroll: true});
    }
  }

  /**
   * What closes the open bar, until it closes. A press outside it, except on its own React button, whose click closes it
   * and must not open it again. Escape: from inside the bar, ahead of a key's tooltip, which would take the overlay
   * keyboard's one turn, and from anywhere else through it. A scroll of the thread, or of anything around it, which
   * would leave the bar away from its button (WhatsApp closes it too). A resize places it again.
   */
  private watchReactionPicker(bar: OverlayRef): void {
    const closed$ = this.reactionBarClosed$;
    bar.outsidePointerEvents().pipe(takeUntil(closed$)).subscribe(event => {
      if (!this.reactionPickerAnchor()?.contains(event.target as Node)) {
        this.closeReactionPicker();
      }
    });
    merge(fromEvent<KeyboardEvent>(bar.overlayElement, 'keydown'), bar.keydownEvents())
      .pipe(filter(event => event.key === 'Escape'), takeUntil(closed$))
      .subscribe(event => {
        event.preventDefault();
        this.closeReactionPicker();
      });
    this.zone.runOutsideAngular(() => {
      fromEvent(document, 'scroll', {capture: true, passive: true})
        .pipe(filter(event => event.target === document || !!(event.target as Node).contains?.(this.reactionPickerAnchor())),
          takeUntil(closed$))
        .subscribe(() => this.zone.run(() => this.closeReactionPicker()));
      fromEvent(window, 'resize').pipe(takeUntil(closed$)).subscribe(() => this.placeReactionPicker());
    });
  }

  /** Whether a chip under a message does anything when pressed: add the same, or take one's own back. */
  canPressReaction(reaction: MessageReaction): boolean {
    return !this.hasLeft && (reaction.mine
      ? this.canRemoveReaction
      : this.canReactWithAnyEmoji || emotionTypeOf(reaction.emoji) !== null);
  }

  /** The chip's tooltip: who reacted, by name (B-10). */
  reactedBy(reaction: MessageReaction): string {
    return reactedByLine(reaction, this.currentUser?.email);
  }

  /** The bar, or a chip under the message: adds the reaction, or takes it back when it is already this user's. */
  react(message: any, glyph: string): void {
    this.closeReactionPicker();
    if (!this.canReact(message) || !glyph) {
      return;
    }
    if (this.reactionsOf(message).some(reaction => sameEmoji(reaction.emoji, glyph) && reaction.mine)) {
      this.unreact(message, glyph);
      return;
    }
    // With B-10 the emoji itself is the reaction; without it, only a glyph the server's EmotionType can carry.
    const anyEmoji = this.canReactWithAnyEmoji;
    const emotionType = emotionTypeOf(glyph);
    if (emotionType === null && !anyEmoji) {
      return;
    }
    this.applyReaction(message.id, glyph, true);
    const command = new AddEmotionCommand(message.id, emotionType ?? 0, 0, anyEmoji ? glyph : undefined);
    command.scope = GraphqlContentScope.getNumberScope(this.currentChatConversation?.scope);
    this.commandService.executeCommand(command).pipe(takeUntil(this.unsubscribe$)).subscribe({
      error: () => {
        this.applyReaction(message.id, glyph, false);
        this.snackbarService.showError('The reaction could not be added.');
      }
    });
  }

  private unreact(message: any, glyph: string): void {
    if (!this.canRemoveReaction) {
      return;
    }
    this.applyReaction(message.id, glyph, false);
    this.messageActions.removeReaction(message.id, glyph).pipe(takeUntil(this.unsubscribe$)).subscribe({
      error: () => {
        this.applyReaction(message.id, glyph, true);
        this.snackbarService.showError('The reaction could not be removed.');
      }
    });
  }

  /** Puts this user in or out of one glyph's count under a message. */
  private applyReaction(messageId: string, glyph: string, on: boolean): void {
    const email = this.currentUser?.email || '';
    const name = `${this.currentUser?.firstName || ''} ${this.currentUser?.lastName || ''}`.trim() || email;
    this.reactions = {...this.reactions, [messageId]: withReaction(this.reactions[messageId], glyph, {email, name}, on, true)};
  }

  /** Forward (B-11), Star (B-12) and Pin (B-13) in the message menu: each only while its flag is on. */
  get canForward(): boolean {
    return this.messengerFlags.isOn('B-11');
  }

  get canStar(): boolean {
    return this.messengerFlags.isOn('B-12');
  }

  get canPin(): boolean {
    return this.messengerFlags.isOn('B-13');
  }

  /**
   * THE STARS AND PINS OF THE OPEN CONVERSATION (ITP-E-194-06). Both are read through the message actions port while
   * their flags are on (B-12, B-13), again for each conversation opened, and keep answering as they change.
   */
  private watchStarsAndPins(): void {
    const conversationId$ = this.collaborationStore.select(getCurrentChatConversation).pipe(
      map(conversation => conversation?.id || null),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$));
    if (this.canStar) {
      conversationId$.pipe(
        switchMap(id => !id ? of([] as string[]) : this.messageActions.starred(id).pipe(catchError(() => of([] as string[])))),
        takeUntil(this.unsubscribe$)
      ).subscribe(ids => this.starredIds = new Set(ids || []));
    }
    if (this.canPin) {
      conversationId$.pipe(
        switchMap(id => !id ? of([] as PinnedMessage[]) : this.messageActions.pinnedMessages(id).pipe(
          catchError(() => of([] as PinnedMessage[])))),
        takeUntil(this.unsubscribe$)
      ).subscribe(pins => {
        this.pinnedMessages = (pins || []).slice(0, 3);
        this.pinnedIndex = this.pinnedIndex < this.pinnedMessages.length ? this.pinnedIndex : 0;
      });
    }
  }

  /**
   * ITP-E-249-05 (B-25): the voice messages of the open conversation this user has played, read with the thread, again for each
   * conversation opened, into the tab's voice player: a voice bubble of someone else's not among them shows the unplayed dot. A
   * read that fails leaves the dots as the player has them.
   */
  private watchPlayedVoice(): void {
    if (!this.voiceOn) {
      return;
    }
    this.collaborationStore.select(getCurrentChatConversation).pipe(
      map(conversation => conversation?.id || null),
      distinctUntilChanged(),
      switchMap(id => !id ? of([] as string[]) : this.messageActions.playedVoice(id).pipe(catchError(() => of([] as string[])))),
      takeUntil(this.unsubscribe$)
    ).subscribe(ids => this.voicePlayer.knowPlayed(ids || []));
  }

  /** The Starred band's paging or empty line (ITP-E-204-06, ITP-E-240-11): 'none' while the band is closed. */
  get starredState(): StarredBandState {
    const conversation = this.currentChatConversation;
    return starredBandState({
      starredIds: this.starredIds,
      loadedIds: new Set((this.messages || []).map(message => message?.id)),
      band: this.band,
      hasOlder: conversation?.lowestSequenceNumber < conversation?.lowestSequenceNumberOfCurrentMessageRange
    });
  }

  isStarred(message: any): boolean {
    return !!message?.id && this.starredIds.has(message.id);
  }

  isPinned(message: any): boolean {
    return !!message?.id && this.pinnedMessages.some(pin => pin.messageId === message.id);
  }

  /**
   * Forward is offered on a message the reader may act on, never on a card row: the server refuses a meeting card's forward
   * (ITP-E-207-05, ChatForward), so the menu does not offer it (ITP-E-240-06).
   */
  canForwardMessage(message: any): boolean {
    return this.canForward && this.canActOn(message) && !message.card?.kind;
  }

  /** Forward: the picker over the chat list the store has loaded, then the port sends it to every one ticked. */
  forwardMessage(message: any): void {
    if (!this.canForwardMessage(message)) {
      return;
    }
    this.collaborationStore.select(getConversationListInfo).pipe(take(1)).subscribe(listInfo => {
      // Never into a support chat: what is said inside the company does not reach Orbitax support by a forward.
      const conversations: ForwardTarget[] = (listInfo?.queryConversations || [])
        .filter(conversation => conversation?.id && !conversation.hasLeft && conversation.sourceType !== 'OrbitaxSupport')
        .map(conversation => ({
          id: conversation.id,
          title: conversation.title,
          titleAvatarInfo: conversation.titleAvatarInfo,
          isGroupConversation: conversation.isGroupConversation,
          sourceType: conversation.sourceType
        }));
      const data: ForwardMessageDialogData = {conversations, preview: this.convertMarkdownToHtml(message.message)};
      this.dialog.open<ForwardMessageDialogComponent, ForwardMessageDialogData, string[]>(ForwardMessageDialogComponent, {
        data,
        autoFocus: false,
        panelClass: 'messenger-forward-dialog'
      }).afterClosed().pipe(
        filter(ids => !!ids?.length),
        switchMap(ids => this.messageActions.forward(message.id, ids).pipe(map(() => ids))),
        takeUntil(this.unsubscribe$)
      ).subscribe({
        next: ids => this.snackbarService.showInformation(
          ids.length === 1 ? 'Message forwarded.' : `Message forwarded to ${ids.length} conversations.`),
        error: () => this.snackbarService.showError('The message could not be forwarded.')
      });
    });
  }

  /** Star or unstar: the mark shows at once and comes off again if the port refuses it. */
  toggleStar(message: any): void {
    if (!this.canStar || !this.canActOn(message)) {
      return;
    }
    const starred = !this.isStarred(message);
    this.applyStar(message.id, starred);
    this.messageActions.setStarred(message.id, starred).pipe(takeUntil(this.unsubscribe$)).subscribe({
      error: () => {
        this.applyStar(message.id, !starred);
        this.snackbarService.showError(starred ? 'The message could not be starred.' : 'The star could not be removed.');
      }
    });
  }

  private applyStar(messageId: string, starred: boolean): void {
    const next = new Set(this.starredIds);
    if (starred) {
      next.add(messageId);
    } else {
      next.delete(messageId);
    }
    this.starredIds = next;
  }

  /** Pin or unpin. Up to 3: pinning a fourth replaces the oldest pin, once the member says so. */
  togglePin(message: any): void {
    const conversationId = this.currentChatConversation?.id;
    if (!this.canPin || !conversationId || !this.canActOn(message)) {
      return;
    }
    const pinning = !this.isPinned(message);
    const confirmed$ = pinning && this.pinnedMessages.length >= 3
      ? this.dialogService.showYesNoDialog('Replace the oldest pin?',
        'A conversation keeps up to 3 pinned messages. Pinning this one unpins the oldest.')
        .pipe(map(state => state?.action === 'OK'))
      : of(true);
    confirmed$.pipe(
      filter(confirmed => confirmed),
      switchMap(() => this.messageActions.setPinned(conversationId, message.id, pinning)),
      takeUntil(this.unsubscribe$)
    ).subscribe({
      next: () => {
        if (pinning) {
          this.pinnedIndex = 0;
        }
      },
      error: () => this.snackbarService.showError(pinning ? 'The message could not be pinned.' : 'The message could not be unpinned.')
    });
  }

  get currentPin(): PinnedMessage | null {
    return this.pinnedMessages[this.pinnedIndex] || this.pinnedMessages[0] || null;
  }

  /**
   * The pinned bar: goes to the pin it shows, paging it in when it is older than those loaded (ITP-E-240-11), then shows
   * the next pin for the next click.
   */
  onPinnedBarClick(): void {
    const pin = this.currentPin;
    if (!pin) {
      return;
    }
    this.jumpToMessage({messageId: pin.messageId});
    if (this.pinnedMessages.length > 1) {
      this.pinnedIndex = (this.pinnedMessages.indexOf(pin) + 1) % this.pinnedMessages.length;
    }
  }

  /** Message info (B-14) in the menu of this user's own message: who received it and who read it. */
  get canShowInfo(): boolean {
    return this.messengerFlags.isOn('B-14');
  }

  showMessageInfo(message: any): void {
    if (!this.canShowInfo || !message?.isMine || !this.canActOn(message)) {
      return;
    }
    this.dialog.open<MessageInfoDialogComponent, MessageInfoDialogData>(MessageInfoDialogComponent, {
      data: {
        messageId: message.id,
        preview: this.convertMarkdownToHtml(message.message),
        senderEmail: message.createdBy?.email || this.currentUser?.email,
        // ITP-E-249-05: a voice message's info also says who played it.
        voice: !!this.voiceOf(message)
      },
      autoFocus: false,
      panelClass: 'messenger-message-info-dialog'
    });
  }

  /** The header's search (B-16): finds text in the conversation, marks it and steps through it. */
  get canSearchThread(): boolean {
    return this.messengerFlags.isOn('B-16');
  }

  /**
   * THE SEARCH IN THE THREAD (ITP-E-194-07). The text goes to `searchMessages(conversationId, text)` through the message
   * actions port once typing settles; the bar starts on the newest match. Opening another conversation closes it.
   */
  private watchThreadSearch(): void {
    if (!this.canSearchThread) {
      return;
    }
    this.collaborationStore.select(getCurrentChatConversation).pipe(
      map(conversation => conversation?.id || null),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$)
    ).subscribe(() => this.closeThreadSearch());
    this.searchText$.pipe(
      debounceTime(250),
      map(text => (text || '').trim()),
      distinctUntilChanged(),
      switchMap(text => {
        const conversationId = this.currentChatConversation?.id;
        return !text || !conversationId ? of({text, hits: [] as MessageHit[]}) : this.messageActions.search(conversationId, text).pipe(
          map(hits => ({text, hits: hits || []})),
          catchError(() => {
            this.snackbarService.showError('The conversation could not be searched.');
            return of({text, hits: [] as MessageHit[]});
          }));
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe(({text, hits}) => this.showSearchHits(text, hits));
  }

  toggleThreadSearch(): void {
    if (this.searchOpen) {
      this.closeThreadSearch();
      return;
    }
    this.searchOpen = true;
    setTimeout(() => this.threadSearchInput?.nativeElement.focus());
  }

  closeThreadSearch(): void {
    this.searchOpen = false;
    this.searchText = '';
    this.searchedText = '';
    this.searchHits = [];
    this.searchIndex = -1;
    this.searchMarkup = {};
    this.searchText$.next('');
  }

  onSearchTextChange(text: string): void {
    this.searchText = text;
    this.searchText$.next(text);
  }

  /** Enter goes to the next older match, Shift+Enter to the next newer. */
  onSearchEnter(event: KeyboardEvent): void {
    event.preventDefault();
    this.stepSearch(event.shiftKey ? 1 : -1);
  }

  /** Steps through the matches: -1 older, +1 newer, round from either end. */
  stepSearch(step: number): void {
    if (!this.searchHits.length) {
      return;
    }
    this.searchIndex = (this.searchIndex + step + this.searchHits.length) % this.searchHits.length;
    this.jumpToHit(this.searchHits[this.searchIndex]);
  }

  get searchCountLabel(): string {
    if (!this.searchedText) {
      return '';
    }
    return this.searchHits.length ? `${this.searchIndex + 1} of ${this.searchHits.length}` : 'No results';
  }

  get currentSearchHitId(): string | null {
    return this.searchOpen ? this.searchHits[this.searchIndex]?.messageId || null : null;
  }

  /** A found message's text with every match marked, or null for any other message, which then shows as always. */
  searchMarkupOf(message: any): string | null {
    if (!this.searchOpen || !this.searchedText || !message?.id || !this.searchHits.some(hit => hit.messageId === message.id)) {
      return null;
    }
    const cached = this.searchMarkup[message.id];
    if (cached && cached.source === message.message && cached.query === this.searchedText) {
      return cached.html;
    }
    const html = ConversationComponent.markMatches(this.convertMarkdownToHtml(message.message), this.searchedText);
    this.searchMarkup[message.id] = {source: message.message, query: this.searchedText, html};
    return html;
  }

  /**
   * Takes the port's answer for the text still in the bar. Rows the thread draws as a call card, a removed or a system
   * message have no text to mark or to step to, so they are left out of the matches.
   */
  private showSearchHits(text: string, hits: MessageHit[]): void {
    if (!this.searchOpen || text !== (this.searchText || '').trim()) {
      return;
    }
    const loaded = new Map((this.messages || []).filter(message => message?.id).map(message => [message.id, message]));
    this.searchHits = hits
      .filter(hit => {
        const message = loaded.get(hit.messageId);
        return !message || (!message.isDeleted && !message.isSystemMessage && !message.callInvitationId);
      })
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    this.searchedText = text;
    this.searchMarkup = {};
    this.searchIndex = this.searchHits.length - 1;
    if (this.searchIndex >= 0) {
      this.jumpToHit(this.searchHits[this.searchIndex]);
    }
  }

  /** A match goes to its message by id and sequence, paged in when it is older than those loaded (ITP-E-240-11). */
  private jumpToHit(hit: MessageHit): void {
    this.jumpToMessage({messageId: hit.messageId, sequence: hit.sequence});
  }

  /** Wraps every case-blind occurrence of the query in the text of a message's HTML in a `mark`; tags are left alone. */
  static markMatches(html: string, query: string): string {
    const needle = (query || '').toLowerCase();
    if (!html || !needle) {
      return html;
    }
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) {
      return html;
    }
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const texts: Text[] = [];
    while (walker.nextNode()) {
      texts.push(walker.currentNode as Text);
    }
    texts.forEach(node => {
      const value = node.nodeValue || '';
      const lower = value.toLowerCase();
      let at = lower.indexOf(needle);
      if (at < 0) {
        return;
      }
      const fragment = doc.createDocumentFragment();
      let from = 0;
      while (at >= 0) {
        fragment.append(value.slice(from, at));
        const mark = doc.createElement('mark');
        mark.className = 'thread-search-mark';
        mark.textContent = value.slice(at, at + needle.length);
        fragment.append(mark);
        from = at + needle.length;
        at = lower.indexOf(needle, from);
      }
      fragment.append(value.slice(from));
      node.replaceWith(fragment);
    });
    return root.innerHTML;
  }

  /** Delivered (B-17) and typing (B-18): each only while its flag is on. */
  get canShowDelivered(): boolean {
    return this.messengerFlags.isOn('B-17');
  }

  get canShowTyping(): boolean {
    return this.messengerFlags.isOn('B-18');
  }

  /**
   * THE THREAD'S SIGNALS (ITP-E-194-08), read through the signals port: the unread divider, placed once as the
   * conversation opens (B-09's position while its flag is on, else today's `unreadMessageCount`); what reached every
   * member (B-17), asked again as messages come and reported for the newest one received here; and who is typing (B-18).
   */
  private watchSignals(): void {
    const conversation$ = this.collaborationStore.select(getCurrentChatConversation).pipe(takeUntil(this.unsubscribe$));
    conversation$.subscribe(conversation => this.placeUnreadDivider(conversation));

    if (this.canShowDelivered) {
      conversation$.pipe(
        map(conversation => ({id: conversation?.id || null, newest: ConversationComponent.newestSequence(conversation?.messages)})),
        distinctUntilChanged((a, b) => a.id === b.id && a.newest === b.newest),
        switchMap(({id}) => {
          // Another conversation's delivery is not this one's.
          if (id !== this.deliveredConversationId) {
            this.deliveredConversationId = id;
            this.deliveredUpTo = null;
          }
          return !id ? of(null) : this.signals.deliveredUpTo(id).pipe(catchError(() => of(null)));
        }),
        takeUntil(this.unsubscribe$)
      ).subscribe(sequence => this.deliveredUpTo = sequence ?? null);
      conversation$.pipe(
        map(conversation => ({
          id: conversation?.id || null,
          sequence: ConversationComponent.newestSequence((conversation?.messages || []).filter(message => !message?.isMine))
        })),
        filter(received => !!received.id && received.sequence !== null),
        distinctUntilChanged((a, b) => a.id === b.id && a.sequence === b.sequence),
        switchMap(received => this.signals.markDelivered(received.id, received.sequence).pipe(catchError(() => EMPTY))),
        takeUntil(this.unsubscribe$)
      ).subscribe();
    }

    if (this.canShowTyping) {
      conversation$.pipe(
        map(conversation => conversation?.id || null),
        distinctUntilChanged(),
        switchMap(id => {
          this.typingMembers = [];
          return !id ? of([] as TypingMember[]) : this.signals.typing(id).pipe(catchError(() => of([] as TypingMember[])));
        }),
        takeUntil(this.unsubscribe$)
      ).subscribe(members => {
        const me = this.currentUser?.email?.toLowerCase();
        this.typingMembers = (members || []).filter(member => member?.email?.toLowerCase() !== me);
      });
    }
  }

  /** Presence and the online count (B-15): only while the flag is on. */
  get canShowPresence(): boolean {
    return this.messengerFlags.isOn('B-15');
  }

  /**
   * PRESENCE IN THE THREAD (ITP-E-194-09), read through the presence port: a direct conversation's header face carries
   * the other person's dot. Read again whenever the conversation changes; another conversation's answer is never shown.
   */
  private watchPresence(): void {
    if (!this.canShowPresence) {
      return;
    }
    this.collaborationStore.select(getCurrentChatConversation).pipe(
      map(conversation => {
        const face = conversation?.titleAvatarInfo?.email?.toLowerCase();
        const direct = !conversation?.isGroupConversation && conversation?.sourceType !== 'DAC6'
          && !!face && face !== this.currentUser?.email?.toLowerCase();
        return {id: conversation?.id || null, face: direct ? face : null};
      }),
      distinctUntilChanged((a, b) => a.id === b.id && a.face === b.face),
      switchMap(({id, face}) => {
        this.headerStatus = null;
        // A group's second line is its members' names on the panel (ITP-E-203-03); the page's online count is
        // watchOnlineCount's.
        return id && face ? this.presence.statuses([{email: face}]).pipe(map(statuses => statuses?.[face] || null),
          catchError(() => of(null))) : of(null);
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe(status => this.headerStatus = status);
  }

  /**
   * A GROUP'S ONLINE COUNT ON THE MESSAGES PAGE (ITP-E-204-09), for carbon's "N members · M online": read again when
   * the conversation or its members change, never showing another conversation's answer.
   */
  private watchOnlineCount(): void {
    if (!this.canShowPresence || !this.isPageHost) {
      return;
    }
    this.collaborationStore.select(getCurrentChatConversation).pipe(
      map(conversation => {
        const group = !!conversation?.isGroupConversation && conversation.sourceType !== 'DAC6';
        const members = group ? this.activeMembersOf(conversation).map(email => ({email})) : [];
        return {id: group ? conversation.id : null, members, key: members.map(member => member.email).join()};
      }),
      distinctUntilChanged((a, b) => a.id === b.id && a.key === b.key),
      switchMap(({id, members}) => {
        this.headerOnline = null;
        return id ? this.presence.onlineCount(id, members).pipe(catchError(() => of(null))) : of(null);
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe(online => this.headerOnline = online);
  }

  /** Opening another conversation closes the band and drops the selection (ITP-E-203-03). */
  private watchBand(): void {
    this.collaborationStore.select(getCurrentChatConversation).pipe(
      map(conversation => conversation?.id || null),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$)
    ).subscribe(() => {
      this.closeBand();
      this.membersOpen = false;
    });
  }

  removeGroupMember(row: GroupPaneRow): void {
    this.changeOneParticipant(row.user, false);
  }

  changeGroupMemberRole(change: GroupPaneRoleChange): void {
    this.changeParticipantRole(change.row.user, change.permissionType);
  }

  addGroupMember(add: GroupPaneAdd): void {
    const member = add.member;
    this.changeOneParticipant({
      userName: member.email, email: member.email, firstName: member.firstName, lastName: member.lastName,
      companyId: member.companyId
    }, true, undefined, add.permissionType);
  }

  /** The highest sequence among the messages, or null when none carries one (a support chat's query has none). */
  static newestSequence(messages: any[] | undefined): number | null {
    return (messages || []).reduce((newest: number | null, message) =>
      typeof message?.sequence === 'number' && (newest === null || message.sequence > newest) ? message.sequence : newest, null);
  }

  /**
   * Puts the divider before the first unread message, once per opening of a conversation, from its first load of
   * messages: that load still carries the unread count, which reading the conversation then clears. It stays while the
   * conversation is open, and goes when this user sends.
   */
  private placeUnreadDivider(conversation: any): void {
    const id = conversation?.id || null;
    if (id !== this.dividerConversationId) {
      this.dividerConversationId = id;
      this.unreadDivider = null;
      this.dividerPending = !!id;
    }
    const messages: any[] = conversation?.messages || [];
    if (!this.dividerPending || !messages.length) {
      return;
    }
    this.dividerPending = false;
    const count = conversation.unreadMessageCount || 0;
    if (this.messengerFlags.isOn('B-09')) {
      this.signals.firstUnreadSequence(id).pipe(take(1), catchError(() => of(null)), takeUntil(this.unsubscribe$))
        .subscribe(sequence => {
          const at = sequence === null ? -1 : messages.findIndex(message => message?.sequence === sequence);
          if (id === this.dividerConversationId && at >= 0 && messages[at].id) {
            const unread = messages.slice(at).filter(message => !message?.isMine && !message?.isSystemMessage).length;
            this.unreadDivider = {messageId: messages[at].id, count: count || unread};
          }
        });
      return;
    }
    this.unreadDivider = ConversationComponent.dividerFrom(messages, count);
  }

  /**
   * Today's position: the count is of other members' messages, so count back over theirs from the newest. With more
   * unread than are loaded, the divider stands before the oldest loaded.
   */
  static dividerFrom(messages: any[], count: number): {messageId: string, count: number} | null {
    if (!(count > 0)) {
      return null;
    }
    let seen = 0;
    for (let at = messages.length - 1; at >= 0; at--) {
      const message = messages[at];
      if (!message?.isMine && !message?.isSystemMessage) {
        seen++;
        if (seen === count) {
          return message?.id ? {messageId: message.id, count} : null;
        }
      }
    }
    const oldest = messages.find(message => message?.id);
    return oldest ? {messageId: oldest.id, count} : null;
  }

  get unreadDividerLabel(): string {
    const count = this.unreadDivider?.count || 0;
    return count === 1 ? '1 unread message' : `${count} unread messages`;
  }

  /** The tick of one of this user's messages; delivered only while B-17 is on. */
  tickFor(message: any): MessageTick | null {
    return tickOf(message, this.canShowDelivered ? this.deliveredUpTo : null);
  }

  /** "Marco is typing…", "Marco and Priya are typing…", or "Several people are typing…"; empty when nobody is. */
  get typingLabel(): string {
    const names = this.typingMembers
      .map(member => (member.name || member.email || '').trim().split(/\s+/)[0])
      .filter(name => !!name);
    if (!names.length) {
      return '';
    }
    if (names.length === 1) {
      return `${names[0]} is typing…`;
    }
    return names.length === 2 ? `${names[0]} and ${names[1]} are typing…` : 'Several people are typing…';
  }

  /** The composer reports this user typing (B-18), at most once per 3 s per conversation, and only with text in it. */
  private reportTyping(html: string): void {
    const id = this.currentChatConversation?.id;
    const text = (html || '').replace(/<[^>]*>|&nbsp;/g, '').trim();
    const now = Date.now();
    if (!this.canShowTyping || this.hasLeft || !id || !text
      || (id === this.typingReportedFor && now - this.typingReportedAt < 3000)) {
      return;
    }
    this.typingReportedFor = id;
    this.typingReportedAt = now;
    this.signals.setTyping(id).pipe(takeUntil(this.unsubscribe$)).subscribe({error: () => undefined});
  }

  /**
   * The name above a run (carbon, ITP-E-203-06): once per run, in a group over every run but this user's own, and over
   * XatBot's in a 1:1 too, the one sender the header never names.
   */
  showSender(index: number): boolean {
    const message = this.messages?.[index];
    return !!message && this.isRunStart(index) && showSenderOf({
      mine: !!message.isMine,
      isXatBot: isAssistantAuthor(message.createdBy)
    }, !!this.currentChatConversation?.isGroupConversation);
  }

  /** The sender line: the author, and on an Assistant's answer who asked it (MOBILE-E-104-03). */
  senderLineOf(message: any): string {
    return senderLine(message?.createdBy, message?.askedBy, this.currentUser?.email);
  }

  /**
   * The line above a call row (ITP-E-240-07, Q23): "You" on the reader's own, the caller's name otherwise (thread-layout
   * `callRowCaller`). In every chat, 1:1 and group: the card has no other line that says who called.
   */
  callRowCallerOf(message: any): string | null {
    return callRowCaller(message);
  }

  /** A message that is a bubble in a run: not removed, not a notice, not a call row. */
  private isRunMessage(message: any): boolean {
    return !!message && !message.isDeleted && !message.isSystemMessage && !message.callInvitationId;
  }

  /** The message at `index` continues the run before it: the same sender, on the same day (a run breaks at a day). */
  private continuesRun(index: number): boolean {
    const message = this.messages?.[index];
    const previous = this.messages?.[index - 1];
    return this.isRunMessage(previous) && this.isRunMessage(message)
      && previous.createdBy?.email?.toLowerCase() === message.createdBy?.email?.toLowerCase()
      && !this.dayLabels[index];
  }

  /** The first bubble of a run: the run's spacing above it, and its sender line. */
  isRunStart(index: number): boolean {
    return !this.continuesRun(index);
  }

  /** The last bubble of a run: the only one whose corner on the run's side is not notched. */
  isRunEnd(index: number): boolean {
    return !this.continuesRun(index + 1);
  }

  /** The day stated above each message (thread-layout `daySeparators`), worked out once per change of the messages. */
  get dayLabels(): (string | null)[] {
    const messages = this.messages || [];
    const key = `${messages.length}|${messages[0]?.id}|${messages[messages.length - 1]?.id}`;
    if (key !== this.dayLabelsKey) {
      this.dayLabelsKey = key;
      this.dayLabelsCache = daySeparators(messages);
    }
    return this.dayLabelsCache;
  }

  /** Everyone still in a conversation, the reader included, once each by lower-case email. */
  private activeMembersOf(conversation: any): string[] {
    return [...new Set<string>((conversation?.roles || [])
      .filter(role => role?.user?.email && !role.hasLeft)
      .map(role => role.user.email.toLowerCase()))];
  }

  /** The members other than the reader, still in the conversation. */
  private get otherMembers(): {email: string, name: string}[] {
    const me = this.currentUser?.email?.toLowerCase();
    return (this.currentChatConversation?.roles || [])
      .filter(role => role?.user?.email && !role.hasLeft && role.user.email.toLowerCase() !== me)
      .map(role => ({email: role.user.email, name: [role.user.firstName, role.user.lastName].filter(Boolean).join(' ')}));
  }

  /**
   * The header's second line (carbon's address): the other person's email in a 1:1; in a group, the others' names on
   * the panel and "N members · M online" on the Messages page (ITP-E-204-09).
   */
  get headerAddress(): string {
    const conversation = this.currentChatConversation;
    if (!conversation || conversation.sourceType === 'DAC6') {
      return '';
    }
    if (!conversation.isGroupConversation) {
      return conversation.titleAvatarInfo?.email || '';
    }
    return this.isPageHost
      ? membersLine(this.activeMembersOf(conversation).length, this.headerOnline)
      : this.otherMembers.map(member => member.name || member.email).join(', ');
  }

  /** A group's header face: two of its other members, kept while the membership stays the same. */
  get headerFaces(): {email: string, name: string}[] {
    const faces = this.otherMembers.slice(0, 2);
    const key = `${this.currentChatConversation?.id}|${faces.map(face => face.email).join()}`;
    if (key !== this.headerFacesKey) {
      this.headerFacesKey = key;
      this.headerFacesCache = faces;
    }
    return this.headerFacesCache;
  }

  get headerMenuItems(): ThreadMenuItem[] {
    const conversation = this.currentChatConversation;
    return threadMenuItems({
      isGroup: !!conversation?.isGroupConversation,
      hasLeft: this.hasLeft,
      maxPermission: this.maxPermission,
      isDefault: !!conversation?.isDefault,
      isContextChat: this.conversationListComponentName !== 'ConversationListComponent',
      flags: {search: this.canSearchThread, starred: this.canStar},
      onPage: this.isPageHost,
      canOpenInMessages: this.holdsMessages && !!conversation?.id
    });
  }

  /** The Messages page header's Search icon (ITP-E-204-09): opens the search bar in place of the band, or closes it. */
  toggleHeaderSearch(): void {
    if (!this.searchOpen) {
      this.closeBand();
    }
    this.toggleThreadSearch();
  }

  onHeaderMenu(item: ThreadMenuItem): void {
    switch (item) {
      case 'open':
        // A tab of its own on the Messages page (ITP-E-268-01); this tab, its page and the panel stay as they were.
        openInMessages(this.currentChatConversation.id);
        break;
      case 'search':
        this.closeBand();
        if (!this.searchOpen) {
          this.toggleThreadSearch();
        }
        break;
      case 'select':
      case 'starred':
        this.closeThreadSearch();
        this.closeReactionPicker(false);
        this.selectedIds = new Set<string>();
        this.band = item;
        if (item === 'starred') {
          // ITP-E-240-11: the band draws only the starred messages loaded; with one still older, it pages by itself.
          this.changedetector.detectChanges();
          this.pageOlderNearTop();
        }
        break;
      case 'members':
        // The panel's only: on the Messages page the members are Details' (ITP-E-204-09).
        this.membersOpen = true;
        break;
      case 'rename':
        this.renameConversation('conversation');
        break;
      case 'leave':
        this.leaveConversation();
        break;
      case 'delete':
        this.confirmDeleteForMe();
        break;
    }
  }

  closeBand(): void {
    this.band = 'none';
    this.selectedIds = new Set<string>();
  }

  /** What Select can pick: a message this user may hide for themself. Call rows, notices and removed ones are not. */
  canSelect(message: any): boolean {
    return !!message?.id && !message.isDeleted && !message.isSystemMessage && !message.callInvitationId
      && !message.isTemporary && !message.isInitialMessage && !message.data;
  }

  /** What is drawn and can be picked: Select all reaches nothing the reader cannot see. */
  private get selectable(): any[] {
    return (this.messages || []).filter(message => this.canSelect(message));
  }

  get allSelected(): boolean {
    const selectable = this.selectable;
    return selectable.length > 0 && selectable.every(message => this.selectedIds.has(message.id));
  }

  get selectionText(): string {
    return selectionLabel(this.selectedIds.size);
  }

  /**
   * A click on a message's row: in the Select band it picks the message, and otherwise it does nothing. It returns nothing on
   * purpose. Angular cancels the click of a template listener that returns `false`, and the row's listener was the expression
   * `band === 'select' && …`, which is `false` outside the band, so every link in a bubble was cancelled: a file sent from the
   * Drive, a link in the words, an attachment drawn as HTML (ITP-E-240-10).
   */
  pickOnClick(message: any): void {
    if (this.band === 'select' && this.canSelect(message)) {
      this.toggleSelected(message);
    }
  }

  toggleSelected(message: any): void {
    const next = new Set(this.selectedIds);
    if (next.has(message.id)) {
      next.delete(message.id);
    } else {
      next.add(message.id);
    }
    this.selectedIds = next;
  }

  selectAll(checked: boolean): void {
    this.selectedIds = new Set(checked ? this.selectable.map(message => message.id) : []);
  }

  /** Hides each picked message for this user only (`DeleteConversationMessageCommand(id, false)`), and stays in Select. */
  deleteSelected(): void {
    (this.messages || []).filter(message => this.selectedIds.has(message.id))
      .forEach(message => this.removeMessage(message, false));
    this.selectedIds = new Set<string>();
  }

  onEditorInnerHtmlEvent(chatText) {
    this.composerChanged$.next();
    this.reportTyping(chatText);
  }

  sendMessage(chatEditorOutput: ChatEditorOutput) {
    super.sendMessage(chatEditorOutput);
    this.messengerDrafts.clear(this.draftConversationId);
    this.unreadDivider = null;
  }


  navigateToConversationList() {
    // The Messages page owns its selection and has no sidebar to swap (ITP-E-194-01).
    if (this.isPageHost) {
      return;
    }
    this.ngOnDestroy();
    this.platformSidebarStore.dispatch(new SetCurrentRightSideComponentAction({
      component: this.conversationListComponentName,
      data: {},
      componentInfo: null
    }));
  }

  resetTitleEditing() {
    this.isTitleEditing = false;
  }

  onClick(event) {
    event.stopPropagation();
  }

  onRevertToOldValue(messageData) {
    this.viewSynchronizerService.handleMessageAction('revertToOldValue', messageData);
  }

  onCancelReply(event) {
    if (event) { this.replyToMessage = null; }
  }

  initComponent() {
    this.chatText = '';
    this.hashTagToShow = '';
    this.showHashTagCard = false;
    this.numOfMessageToDisplay = 10;
    this.contactStore.dispatch(new GetAllSharedContactsAction());
  }

  navigateToSourceOfChat() {
    if(this.currentChatConversation.sourceType=="OrbitaxSupport"){
      const taskId = this.currentChatConversation.sourceInfo.sourceInfoDictionary.find(item => item.Key === 'TaskId')?.Value;
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`/ticket/${taskId}`])
      );
      window.open(url, '_blank');
    }
    else{
      this.collaborationService.navigateToSource(this.currentChatConversation.sourceType, this.currentChatConversation.sourceInfo);
     }
  }

  /**
   * People with no record in the conversation join it. As every membership save (ITP-E-204-07): the records of the one
   * conversation open now, as copies, sent to its id after the answer, and the store's conversation left untouched.
   */
  manageRolesFromArray(rulesArray: any) {
    const conversation = this.currentChatConversation;
    if (!rulesArray?.length || !conversation?.roles) {
      return;
    }
    const known = (email: string) => conversation.roles.some(role => role.user?.email?.toLowerCase() === email?.toLowerCase());
    const fresh = rulesArray.filter(rule => rule?.user?.email && !known(rule.user.email));
    if (!fresh.length) {
      return;
    }
    const plan = planMembership(conversation.roles,
      [...activeParticipants(conversation.roles), ...fresh.map(rule => ({user: rule.user, permissionType: rule.permissionType}))],
      this.currentUser);
    this.dialogService.showYesNoDialog('Confirm show previous messages', ` Do you want to show the previous messages?`)
      .pipe(takeUntil(this.unsubscribe$)).subscribe(dialogState => {
        const makePreviousConversationVisible = dialogState.action === 'OK';
        this.collaborationStore.dispatch(new ManageParticipantsAction(
          new ManageParticipantsCommand(conversation.id, plan.roles, makePreviousConversationVisible)));
      });
  }

  setCurrentContext(contextId) {
  }

  setCurrentConversation(conversationId) {
  }

  postMessage(conversationId, message) {
  }

  setComponentToInitialState() {
  }

  onClickXMark() {
    this.closeConversationWindow$.emit(true);
  }
 addXatBotToContacts() {
     const xatBotExists = this.allContacts.some(contact => contact.name === 'XatBot');
     if (!xatBotExists) {
       this.allContacts.push(new ContactData({ name: 'XatBot', firstName: 'XatBot', user: {name: 'XatBot', firstName: 'XatBot', email: 'xatbot@orbitax.com'}}));
     }
   }

  convertMarkdownToHtml(message: string): string {
      if (!message) return '';
      try {
        return marked.parse(message) as string;
      } catch (error) {
        console.error('Error parsing markdown:', error);
        return message;
      }
    }
}
